import { useEffect, useState } from "react";
import { useNavigate, Link } from "react-router-dom";
import { motion } from "framer-motion";
import { toast } from "sonner";
import { Film, ShoppingBag, Users, Sparkles, Crown, Check } from "lucide-react";
import { api, formatApiError, setToken } from "../lib/api";
import { useAuth } from "../context/AuthContext";
import { Button } from "../components/ui/button";
import { Input } from "../components/ui/input";
import { Label } from "../components/ui/label";
import { ACCENT, planPrice, BillingToggle, PlanComparison } from "../components/plans";
import { BubzLogo } from "../components/BubzLogo";
import ForgotPassword from "../components/ForgotPassword";
import ComingSoonPopup from "../components/ComingSoonPopup";

export default function Landing() {
  const { user, setUser } = useAuth();
  const navigate = useNavigate();
  const [form, setForm] = useState({ email: "", password: "" });
  const [loading, setLoading] = useState(false);
  const [forgotOpen, setForgotOpen] = useState(false);

  useEffect(() => {
    if (user && user !== false) navigate(user.role === "admin" ? "/admin" : "/profiles", { replace: true });
  }, [user, navigate]);

  const submit = async (e) => {
    e.preventDefault();
    setLoading(true);
    try {
      const { data } = await api.post("/auth/login", form);
      setToken(data.token);
      setUser(data);
      toast.success(`Welcome${data.name ? ", " + data.name.split(" ")[0] : ""}! 🎉`);
      navigate(data.role === "admin" ? "/admin" : "/profiles", { replace: true });
    } catch (err) {
      toast.error(formatApiError(err.response?.data?.detail) || "Something went wrong");
    } finally {
      setLoading(false);
    }
  };

  const googleLogin = () => {
    // REMINDER: DO NOT HARDCODE THE URL, OR ADD ANY FALLBACKS OR REDIRECT URLS, THIS BREAKS THE AUTH
    const redirectUrl = window.location.origin + "/profiles";
    window.location.href = `https://auth.emergentagent.com/?redirect=${encodeURIComponent(redirectUrl)}`;
  };


  const [plans, setPlans] = useState([]);
  const [cycle, setCycle] = useState("monthly");
  useEffect(() => { api.get("/subscription/plans").then(({ data }) => setPlans(data)).catch(() => {}); }, []);

  return (
    <div className="min-h-screen">
      <ComingSoonPopup />
    <ForgotPassword open={forgotOpen} onClose={() => setForgotOpen(false)} initialEmail={form.email} />
    <div className="grid lg:grid-cols-2 lg:min-h-screen">
      {/* Left hero */}
      <div className="relative hidden lg:flex flex-col justify-between p-12 overflow-hidden bg-[#FF5A79]">
        <div className="absolute -top-20 -right-20 h-80 w-80 rounded-full bg-[#FFC02D] opacity-40 blur-2xl" />
        <div className="absolute bottom-10 -left-16 h-72 w-72 rounded-full bg-[#2AB7CA] opacity-40 blur-2xl" />
        <div className="relative z-10">
          <BubzLogo size="xl" light testid="landing-hero-logo" />
          <p className="mt-2 text-white/90 font-medium">Watch. Play. Share. Grow.</p>
        </div>
        <div className="relative z-10 space-y-5">
          <h1 className="text-4xl xl:text-5xl font-display font-bold text-white leading-tight">
            A safe, joyful world for curious kids & caring parents.
          </h1>
          <div className="flex flex-wrap gap-3">
            {[["Stream shows", Film], ["Buy & sell", ShoppingBag], ["Family feed", Users]].map(([t, Icon]) => (
              <div key={t} className="flex items-center gap-2 bg-white/20 backdrop-blur rounded-full px-4 py-2 text-white font-semibold">
                <Icon className="h-4 w-4" /> {t}
              </div>
            ))}
          </div>
        </div>
        <div className="relative z-10 flex items-center gap-2 text-white/80">
          <Sparkles className="h-4 w-4" /> Educational content curated for ages 3–12
        </div>
      </div>

      {/* Right form */}
      <div className="flex items-center justify-center p-6 sm:p-12">
        <motion.div
          initial={{ opacity: 0, y: 20 }} animate={{ opacity: 1, y: 0 }} transition={{ duration: 0.5 }}
          className="w-full max-w-md"
        >
          <div className="lg:hidden mb-6"><BubzLogo size="lg" testid="landing-mobile-logo" /></div>
          <h2 className="text-3xl font-display font-bold text-slate-900">
            Welcome back! 👋
          </h2>
          <p className="text-slate-500 mt-1">
            Sign in to your parent account.
          </p>

          <form onSubmit={submit} className="mt-8 space-y-4" data-testid="auth-form">
            <div>
              <Label htmlFor="email">Email</Label>
              <Input id="email" type="email" data-testid="email-input" className="mt-1 rounded-xl h-12" required
                value={form.email} onChange={(e) => setForm({ ...form, email: e.target.value })} placeholder="you@email.com" />
            </div>
            <div>
              <Label htmlFor="password">Password</Label>
              <Input id="password" type="password" data-testid="password-input" className="mt-1 rounded-xl h-12" required
                value={form.password} onChange={(e) => setForm({ ...form, password: e.target.value })} placeholder="••••••••" />
            </div>
            <Button type="submit" disabled={loading} data-testid="submit-auth-btn"
              className="w-full h-12 rounded-xl text-base font-bold bg-[#FF5A79] hover:bg-[#e04865] shadow-lg shadow-rose-500/25 active:scale-95 transition-all">
              {loading ? "Please wait…" : "Sign in"}
            </Button>
            <button type="button" data-testid="forgot-password-link" onClick={() => setForgotOpen(true)}
              className="block mx-auto text-sm font-semibold text-[#9B5DE5] hover:underline">
              Forgot your password?
            </button>
          </form>

          <div className="flex items-center gap-3 my-5">
            <div className="h-px flex-1 bg-slate-200" />
            <span className="text-xs uppercase tracking-wider text-slate-400 font-semibold">or</span>
            <div className="h-px flex-1 bg-slate-200" />
          </div>

          <Button onClick={googleLogin} variant="outline" data-testid="google-login-btn"
            className="w-full h-12 rounded-xl font-semibold border-2 active:scale-95 transition-all">
            <img alt="g" src="https://www.gstatic.com/firebasejs/ui/2.0.0/images/auth/google.svg" className="h-5 w-5 mr-2" />
            Continue with Google
          </Button>
          <p className="mt-3 text-center text-xs text-slate-400" data-testid="auth-legal-note">
            By continuing you agree to our{" "}
            <Link to="/terms" data-testid="login-terms-link" className="underline hover:text-slate-600">Terms</Link> and{" "}
            <Link to="/privacy" data-testid="login-privacy-link" className="underline hover:text-slate-600">Privacy Policy</Link>.
          </p>
        </motion.div>
      </div>
    </div>

    {/* Public pricing — front page plans */}
    <section className="bg-white border-t border-amber-100 px-6 py-16" data-testid="landing-pricing">
      <div className="max-w-6xl mx-auto text-center">
        <span className="inline-flex items-center gap-2 rounded-full bg-[#9B5DE5]/10 text-[#9B5DE5] px-4 py-1.5 text-sm font-bold"><Crown className="h-4 w-4" /> Bubz + streaming plans</span>
        <h2 className="mt-4 text-3xl sm:text-4xl font-display font-bold text-slate-900">Simple plans for happy families</h2>
        <p className="text-slate-500 mt-3 max-w-xl mx-auto">All plans include a 7-day free trial — cancel anytime.</p>
        <div className="mt-6 flex justify-center"><BillingToggle cycle={cycle} setCycle={setCycle} /></div>

        <div className="grid sm:grid-cols-2 lg:grid-cols-3 gap-5 mt-10 text-left">
          {plans.map((p) => {
            const accent = ACCENT[p.id] || "#FF5A79";
            const pr = planPrice(p, cycle);
            const paid = p.price > 0;
            return (
              <div key={p.id} data-testid={`landing-plan-${p.id}`}
                className={`rounded-3xl border-2 p-6 flex flex-col bg-white ${p.id === "standard" ? "shadow-xl" : ""}`}
                style={{ borderColor: p.id === "standard" ? accent : "#f1e9d9" }}>
                <div className="flex items-center gap-2 flex-wrap mb-2 min-h-[24px]">
                  {p.id === "standard" && <span className="rounded-full bg-[#FF5A79] text-white px-3 py-0.5 text-xs font-bold">Most popular</span>}
                  {paid && <span className="rounded-full bg-emerald-100 text-emerald-700 px-2.5 py-0.5 text-xs font-bold">7-day free trial</span>}
                </div>
                <div className="font-display text-2xl font-bold text-slate-900">{p.name}</div>
                <div className="mt-1">
                  {pr.label === "Free" ? <span className="text-3xl font-display font-bold" style={{ color: accent }}>Free</span>
                    : <><span className="text-4xl font-display font-bold text-slate-900">{pr.label}</span><span className="text-slate-400">{pr.suffix}</span></>}
                </div>
                {pr.note && <div className="text-xs font-semibold text-emerald-600 mt-0.5">{pr.note}</div>}
                <div className="text-xs font-semibold text-slate-400 mt-1">{p.quality} · {p.devices}</div>
                <ul className="mt-4 space-y-2 flex-1">
                  {p.features.map((j, k) => (
                    <li key={k} className="flex items-start gap-2 text-sm text-slate-600"><Check className="h-4 w-4 mt-0.5 shrink-0" style={{ color: accent }} /> {j}</li>
                  ))}
                </ul>
                <div className="mt-6 text-center text-sm font-bold rounded-xl py-3 text-white" style={{ backgroundColor: accent }}>
                  7-day free trial
                </div>
              </div>
            );
          })}
        </div>

        <div className="mt-14 text-left">
          <h3 className="text-2xl font-display font-bold text-slate-900 text-center mb-6">Compare every plan</h3>
          <PlanComparison plans={plans} cycle={cycle} />
        </div>
      </div>
    </section>
    </div>
  );
}
